<template>
  <FriedMessage v-if="hasQesFiles">
    {{ t('qes_file_info_message') }}
  </FriedMessage>
  <UploadButton
    v-else-if="isSmallScreenAndDown"
    :accepted-file-types="qesFileType.toString()"
    @select-file="uploadQesFile"
  >
    <FriedRoundedButton
      class="upload-button"
      icon-align-right
      data-external="create-document-upload-qes-button"
    >
      {{ $t('browse-to-choose-a-file') }}
      <template #icon>
        <FriedIcon icon="upload" />
      </template>
    </FriedRoundedButton>
  </UploadButton>
  <FriedDropzone
    v-else
    class="qes-dropzone"
    :accepted-file-types="qesFileType.toString()"
    :disabled="uploadingQesFile"
    @update:model-value="uploadQesFile"
    @error="handleError"
  >
    <template v-if="uploadingQesFile" #illustration>
      <FriedSpinner />
    </template>
    <template v-if="!!errorMessage.title" #title>
      {{ errorMessage.title }}
    </template>
    <template v-else #title>
      {{ dropzoneLocalization().drag_and_drop_or }}
      {{ dropzoneLocalization().browse_to_choose_a_file }}
    </template>
    <template #description>
      {{ t('supported_file_types', { fileTypes: qesFileType.join(', ') }) }}
      <br />
      {{ `${dropzoneLocalization().max_file_size}: 1MB` }}
    </template>
  </FriedDropzone>
  <DocumentQesFiles
    v-if="hasQesFiles"
    class="create-document-modal-qes-file-list"
    compact
    :files="qesFiles"
    is-editable
    @delete="deleteQesFile(0, $event)"
  />
</template>

<script lang="ts" setup>
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { computed, ref, watch } from 'vue';
import { qesFileType } from '@getaccept/lib-shared/src/file/constants/accepted-file-types';
import Translations from '@getaccept/lib-shared/src/constants/translations.constants';
import DocumentQesFiles from '@getaccept/lib-shared/src/qes/components/DocumentQesFiles.vue';
import type { DocumentQesFile } from '@getaccept/lib-shared/src/qes/types/document-qes-file';
import { useAsyncState } from '@vueuse/core';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { useCreateQesFile } from '../composables/upload-qes-files.composable';
import { QesService } from '../../documents/qes/services/qes.service';
import UploadButton from '../../components/UploadButton.vue';

const {
  documentId,
  qes = false,
  qesFiles = [],
} = defineProps<{
  documentId: string;
  qesFiles?: DocumentQesFile[];
  qes?: boolean;
}>();
const emit = defineEmits<{
  'reload-qes-files': [];
}>();

const hasQesFiles = computed(() => qesFiles?.length > 0);
const { isSmallScreenAndDown } = useBreakpoints();

const errorMessage = ref({ title: '', description: '' });

const handleError = (fileNames: string[]) => {
  if (fileNames.length) {
    errorMessage.value = {
      title: t('unsupported-format-title'),
      description: t('unsupported-format-description'),
    };
  } else {
    errorMessage.value = { title: '', description: '' };
  }
};

const {
  execute: uploadQesFileMutation,
  isLoading: uploadingQesFile,
  isReady: qesFileUploaded,
} = useCreateQesFile(documentId);

watch(qesFileUploaded, () => {
  if (!qesFileUploaded.value) return;
  emit('reload-qes-files');
});

const uploadQesFile = (fileList: File[]) => {
  if (!fileList.length) return;

  if (fileList.length > 1) {
    errorMessage.value = {
      title: t('too-many-files-title'),
      description: t('too-many-files-description', { 'max-files': 1 }),
    };
    return;
  }

  uploadQesFileMutation(0, fileList[0], qes);
};

const { execute: deleteQesFile } = useAsyncState(
  (id: string) => QesService.deleteQesFile(documentId, id),
  null,
  { immediate: false, onSuccess: () => emit('reload-qes-files') }
);

const { dropzoneLocalization } = Translations;
</script>

<style lang="scss" scoped>
.dropzone-container {
  &.qes-dropzone {
    flex: 0 1 fit-content;
  }
}

.create-document-modal-qes-file-list {
  margin: var(--spacing-100) 2px;
}
</style>
